import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import { formatAmountForInput, parseFormattedAmount } from '../../utils/amountFormatter';

const AmountInput = ({
  value = '',
  onChange,
  placeholder = 'Montant',
  error = false,
  helperText,
  inputProps = {},
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Initialize display value when component mounts or value changes externally
  useEffect(() => {
    if (!isEditing && value !== '' && value != null) {
      const numericValue = typeof value === 'string' ? parseFloat(value) : value;
      if (!isNaN(numericValue)) {
        setDisplayValue(formatAmountForInput(numericValue));
      }
    }
  }, [value, isEditing]);

  const handleChange = (event) => {
    const inputValue = event.target.value;
    
    // Allow empty input
    if (inputValue === '') {
      setDisplayValue('');
      onChange({ target: { value: '' } });
      return;
    }

    // Remove all non-numeric characters except decimal points (. and ,)
    const cleanValue = inputValue.replace(/[^\d.,]/g, '');
    
    // Normalize decimal point to dot (replace comma with dot)
    const normalizedValue = cleanValue.replace(',', '.');
    
    // Prevent multiple decimal points
    const parts = normalizedValue.split('.');
    if (parts.length > 2) {
      return; // Don't update if multiple decimal points
    }

    // Limit decimal places to 2
    if (parts.length === 2 && parts[1].length > 2) {
      return; // Don't update if more than 2 decimal places
    }

    // Parse to number
    const numericValue = parseFloat(normalizedValue);
    if (!isNaN(numericValue)) {
      // While editing, show the raw input (with comma normalized to dot)
      setDisplayValue(normalizedValue);
      
      // Send the numeric value to parent
      onChange({ target: { value: numericValue.toString() } });
    }
  };

  const handleFocus = () => {
    setIsEditing(true);
    // Show raw value for editing
    if (displayValue !== '') {
      const numericValue = parseFormattedAmount(displayValue);
      if (!isNaN(numericValue)) {
        setDisplayValue(numericValue.toString());
      }
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    // Format properly on blur
    if (displayValue !== '') {
      const numericValue = parseFormattedAmount(displayValue);
      if (!isNaN(numericValue)) {
        setDisplayValue(formatAmountForInput(numericValue));
      }
    }
  };

  const handleKeyDown = (event) => {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
        (event.keyCode === 65 && (event.ctrlKey || event.metaKey)) ||
        (event.keyCode === 67 && (event.ctrlKey || event.metaKey)) ||
        (event.keyCode === 86 && (event.ctrlKey || event.metaKey)) ||
        (event.keyCode === 88 && (event.ctrlKey || event.metaKey)) ||
        // Allow: home, end, left, right, down, up
        (event.keyCode >= 35 && event.keyCode <= 40)) {
      return;
    }
    
    // Allow numbers (0-9) on main keyboard and numpad
    if ((event.keyCode >= 48 && event.keyCode <= 57) || 
        (event.keyCode >= 96 && event.keyCode <= 105)) {
      return;
    }
    
    // Allow decimal points - be very permissive
    if (event.key === '.' || event.key === ',' || 
        event.keyCode === 190 || event.keyCode === 188 ||
        event.keyCode === 110) { // numpad decimal
      return;
    }
    
    // Block everything else
    event.preventDefault();
  };

  return (
    <TextField
      {...props}
      fullWidth
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      type="text"
      inputProps={{ 
        inputMode: 'numeric',
        pattern: '[0-9]*',
        ...inputProps 
      }}
      placeholder={placeholder}
      error={error}
      helperText={helperText}
      sx={{
        '& .MuiOutlinedInput-root': {
          height: '48px',
          paddingLeft: '12px',
          paddingRight: '12px',
          fontSize: '16px',
          fontWeight: 600,
          fontFamily: 'Montserrat, sans-serif',
          backgroundColor: '#ffffff',
          border: error ? '2px solid #EF456E' : (displayValue && displayValue.toString().trim() !== '') ? '2px solid #007BC6' : '1px solid #E3E3E3',
          borderRadius: '8px',
          '&:hover': {
            border: error ? '2px solid #EF456E' : '1px solid #007BC6',
          },
          '&.Mui-focused': {
            border: error ? '2px solid #EF456E' : '1px solid #007BC6',
          },
          '& fieldset': {
            border: 'none',
          },
        },
        '& .MuiInputBase-input': {
          padding: '0 !important',
          margin: '0 !important',
          textIndent: '0 !important',
          '&::placeholder': {
            color: '#A3A3A3',
            opacity: 1,
          },
        },
        '& .MuiFormHelperText-root': {
          fontFamily: 'Montserrat',
          fontSize: '12px',
          fontWeight: 500,
          marginTop: '4px',
          marginLeft: '0px',
        },
      }}
    />
  );
};

export default AmountInput;